// src/hooks/useSizingStateV2.ts

import { useCallback, useState } from 'react';
import type { ScenarioState, Tradeoffs, UserType, ScenarioPreset } from '@/types';

/**
 * Quick start presets used by the UI.
 * These stay as they are and can override the default scenario
 * when the user clicks a preset.
 */
const PRESETS: ScenarioPreset[] = [
  {
    id: 'hyper-growth',
    label: 'Hyper-Growth',
    description: 'Aggressive scaling for fast-growing teams.',
    tradeoffs: { turns: 4, batch: 6, precision: 8, concurrentUsers: 2000 },
    users: [
      { uid: 'enterprise-analyst', id: 'enterprise-analyst', profileId: 'enterprise-analyst', label: 'Enterprise Analyst', pct: 55 },
      { uid: 'casual-qa', id: 'casual-qa', profileId: 'casual-qa', label: 'Casual Q&A', pct: 25 },
      { uid: 'email-drafter', id: 'email-drafter', profileId: 'email-drafter', label: 'Email Drafter', pct: 20 },
    ],
  },
  {
    id: 'steady-enterprise',
    label: 'Steady Enterprise',
    description: 'Balanced workloads with predictable demand.',
    tradeoffs: { turns: 3, batch: 4, precision: 8, concurrentUsers: 2000 },
    users: [
      { uid: 'enterprise-analyst', id: 'enterprise-analyst', profileId: 'enterprise-analyst', label: 'Enterprise Analyst', pct: 45 },
      { uid: 'casual-qa', id: 'casual-qa', profileId: 'casual-qa', label: 'Internal Q&A', pct: 35 },
      { uid: 'email-drafter', id: 'email-drafter', profileId: 'email-drafter', label: 'Content Studio', pct: 20 },
    ],
  },
  {
    id: 'peak-performance',
    label: 'Peak Performance',
    description: 'Prioritize throughput for bursty peaks.',
    tradeoffs: { turns: 2, batch: 8, precision: 8, concurrentUsers: 2000 },
    users: [
      { uid: 'knowledge-worker', id: 'knowledge-worker', profileId: 'knowledge-worker', label: 'Knowledge Worker', pct: 40 },
      { uid: 'developer-assistant', id: 'developer-assistant', profileId: 'developer-assistant', label: 'Developer Assistant', pct: 40 },
      { uid: 'casual-qa', id: 'casual-qa', profileId: 'casual-qa', label: 'Support Edge', pct: 20 },
    ],
  },
  {
    id: 'specialized-services',
    label: 'Specialized Services',
    description: 'Smaller teams with deep context needs.',
    tradeoffs: { turns: 5, batch: 3, precision: 16, concurrentUsers: 2000 },
    users: [
      { uid: 'enterprise-analyst', id: 'enterprise-analyst', profileId: 'enterprise-analyst', label: 'Research Analyst', pct: 35 },
      { uid: 'legal-assistant', id: 'legal-assistant', profileId: 'legal-assistant', label: 'Legal Review', pct: 30 },
      { uid: 'knowledge-worker', id: 'knowledge-worker', profileId: 'knowledge-worker', label: 'Field Support', pct: 20 },
      { uid: 'email-drafter', id: 'email-drafter', profileId: 'email-drafter', label: 'Campaign Studio', pct: 15 },
    ],
  },
];

/**
 * Helper to build a scenario from a preset.
 * Used only when the user selects a quick start scenario.
 */
const buildScenarioFromPreset = (preset: ScenarioPreset): ScenarioState => {
  const base: ScenarioState = {
    selectedModelId: null,
    totalPct: 0,
    warningUnderPct: false,
    blockedOverPct: false,
    sessionMode: 'stateful',
    tradeoffs: {
      concurrentUsers: 0,
      precision: 16,
      batch: 1,
      turns: 1,
      latencyTolerance: 'balanced',
    } as Tradeoffs,
    users: [],
    advanced: {
      latencyMs: 0,
      latencyTargetMs: 0,
      trafficVolatility: 0.2,
      growthCagrPct: 0,
      finalPlanPolicy: 'max',
      bridgingEnabled: false,
      bridgingEfficiency: 0.9,
      bridgingOverheadGiB: 0,
      kvPrecisionBits: 16,
      kvSafetyFactor: 1.0,
      latencyPolicy: 'streaming',
      ttftFloorMs: 0,
    },
  };

  const tradeoffs: Tradeoffs = {
    ...base.tradeoffs,
    ...preset.tradeoffs,
  };

  const users: UserType[] = preset.users.map((u, idx) => {
    const uid = u.uid ?? u.id ?? `preset-${preset.id}-user-${idx + 1}`;
    return {
      uid,
      id: u.id ?? uid,
      label: u.label,
      pct: u.pct ?? 0,
      inTok: u.inTok ?? 0,
      outTok: u.outTok ?? 0,
      read_sec: (u as any).read_sec ?? 0,
      digest_sec: (u as any).digest_sec ?? 0,
      profileId: u.profileId,
    };
  });

  const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);

  return {
    ...base,
    tradeoffs,
    users,
    totalPct,
    warningUnderPct: totalPct < 100,
    blockedOverPct: totalPct > 100,
  };
};

/**
 * Initial scenario used on first load.
 *
 * Sizing defaults:
 * 1) Personas: seeded from useSizingEngineV2 (Enterprise Analyst 50%, Casual Q&A 30%, Email Drafter 20%)
 * 2) Concurrent users: 2000
 * 3) Batch size: 256
 * 4) Latency: Responsive  -> latencyTolerance: 'balanced'
 * 5) Accuracy: 8 bit      -> precision: 8
 * 6) Chat memory: 3 turns -> turns: 3
 * 7) Model: Llama 3 70B   -> selectedModelId: 'llama3-70b'
 *
 * Users are intentionally empty so the unified sizing hook is the
 * single source of truth for seeding personas.
 */
const buildInitialScenario = (): ScenarioState => {
  const users: UserType[] = [];
  const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);

  return {
    selectedModelId: 'llama3-70b',
    totalPct,
    warningUnderPct: totalPct < 100,
    blockedOverPct: totalPct > 100,
    sessionMode: 'stateful',
    tradeoffs: {
      concurrentUsers: 2000,
      precision: 8,
      batch: 256,
      turns: 3,
      latencyTolerance: 'balanced',
    } as Tradeoffs,
    users,
    advanced: {
      latencyMs: 3000,
      latencyTargetMs: 3000,
      trafficVolatility: 0.2,
      growthCagrPct: 0,
      finalPlanPolicy: 'max',
      bridgingEnabled: false,
      bridgingEfficiency: 0.9,
      bridgingOverheadGiB: 0,
      kvPrecisionBits: 16,
      kvSafetyFactor: 1.0,
      latencyPolicy: 'streaming',
      ttftFloorMs: 450,
    },
  };
};

export function useSizingStateV2() {
  const [scenario, setScenario] = useState<ScenarioState>(buildInitialScenario);

  const setTradeoff = useCallback(
    <K extends keyof Tradeoffs>(key: K, value: Tradeoffs[K]) => {
      setScenario(prev => ({
        ...prev,
        tradeoffs: { ...prev.tradeoffs, [key]: value },
      }));
    },
    [],
  );

  const setAdvanced = useCallback(
    <K extends keyof ScenarioState['advanced']>(
      key: K,
      value: ScenarioState['advanced'][K],
    ) => {
      setScenario(prev => ({
        ...prev,
        advanced: { ...prev.advanced, [key]: value },
      }));
    },
    [],
  );

  const updateUser = useCallback((uid: string, patch: Partial<UserType>) => {
    setScenario(prev => {
      const users = prev.users.map(u =>
        u.uid === uid || u.id === uid
          ? {
              ...u,
              ...patch,
              uid: u.uid ?? uid,
              id: u.id ?? uid,
            }
          : u,
      );
      const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);
      return {
        ...prev,
        users,
        totalPct,
        warningUnderPct: totalPct < 100,
        blockedOverPct: totalPct > 100,
      };
    });
  }, []);

  const removeUser = useCallback((uid: string) => {
    setScenario(prev => {
      const users = prev.users.filter(u => u.uid !== uid && u.id !== uid);
      const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);
      return {
        ...prev,
        users,
        totalPct,
        warningUnderPct: totalPct < 100,
        blockedOverPct: totalPct > 100,
      };
    });
  }, []);

  const addUser = useCallback((user?: Partial<UserType>) => {
    setScenario(prev => {
      const incomingId = user?.id;
      const incomingUid = (user as any)?.uid ?? incomingId;
      const incomingProfileId = user?.profileId;

      // If we already have this logical persona, silently ignore
      if (incomingUid && prev.users.some(u => u.uid === incomingUid || u.id === incomingUid)) {
        return prev;
      }
      if (
        incomingProfileId &&
        incomingProfileId !== 'custom' &&
        prev.users.some(u => u.profileId === incomingProfileId)
      ) {
        return prev;
      }

      const nextIndex = prev.users.length + 1;
      const baseId = incomingId ?? incomingUid ?? `user-${nextIndex}`;
      const baseUid = incomingUid ?? baseId;

      const nextUser: UserType = {
        uid: baseUid,
        id: baseId,
        label: 'Persona',
        pct: 0,
        inTok: 0,
        outTok: 0,
        read_sec: 0,
        digest_sec: 0,
        ...user,
      };

      const users = [...prev.users, nextUser];
      const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);

      return {
        ...prev,
        users,
        totalPct,
        warningUnderPct: totalPct < 100,
        blockedOverPct: totalPct > 100,
      };
    });
  }, []);

  const setSelectedModelId = useCallback((modelId: string | null) => {
    setScenario(prev => ({ ...prev, selectedModelId: modelId }));
  }, []);

  /**
   * Apply a quick-start preset by id or by full preset object.
   */
  const applyPreset = useCallback((presetOrId: ScenarioPreset | string) => {
    const preset =
      typeof presetOrId === 'string'
        ? PRESETS.find(p => p.id === presetOrId) ?? null
        : presetOrId;

    if (!preset) {
      return;
    }

    setScenario(prev => {
      const next = buildScenarioFromPreset(preset);
      return {
        ...next,
        selectedModelId: prev.selectedModelId ?? next.selectedModelId,
      };
    });
  }, []);

  const resetScenario = useCallback(() => {
    setScenario(buildInitialScenario());
  }, []);

  const shareScenario = useCallback(async () => false, []);

  /**
   * Bulk replace all users with a new set (used by team type selection).
   */
  const setUsers = useCallback((users: UserType[]) => {
    setScenario(prev => {
      const totalPct = users.reduce((sum, u) => sum + (u.pct ?? 0), 0);
      return {
        ...prev,
        users,
        totalPct,
        warningUnderPct: totalPct < 100,
        blockedOverPct: totalPct > 100,
      };
    });
  }, []);

  return {
    scenario,
    setTradeoff,
    setAdvanced,
    updateUser,
    removeUser,
    addUser,
    setUsers,
    setSelectedModelId,
    applyPreset,
    resetScenario,
    shareScenario,
  };
}

export { PRESETS as SCENARIO_PRESETS };
export type UseSizingStateV2Return = ReturnType<typeof useSizingStateV2>;
